import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, Easing } from "remotion";
import { loadFont } from "@remotion/google-fonts/TiroDevanagariHindi";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";
import type { MantraCardData } from "./types";

const { fontFamily: devFont } = loadFont("normal", { weights: ["400"], subsets: ["devanagari"] });
const { fontFamily: inter } = loadInter("normal", { weights: ["300"], subsets: ["latin"] });

const GOLD = "#FFD700";

// 1280 treated as three equal 427px-wide canvases
// Each word is horizontally centred in its section, gentle diagonal in Y
const SLOTS = [
  { x: Math.round(1280 / 6),      y: 160 }, // section 1 centre ≈ 213
  { x: Math.round(1280 / 2),      y: 290 }, // section 2 centre = 640
  { x: Math.round(1280 * 5 / 6),  y: 430 }, // section 3 centre ≈ 1067
];

const MEANINGS = ["Truth", "The Divine", "Beauty"];

const ScalesIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <line x1="20" y1="6" x2="20" y2="35" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="14" y1="35" x2="26" y2="35" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="6" y1="10" x2="34" y2="10" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="20" cy="10" r="2" fill="#FFD700"/>
    <line x1="7" y1="10" x2="7" y2="23" stroke="#FFD700" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="2,1.5"/>
    <line x1="33" y1="10" x2="33" y2="23" stroke="#FFD700" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="2,1.5"/>
    <line x1="3" y1="23" x2="11" y2="23" stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3,23 Q7,30 11,23" stroke="#FFD700" strokeWidth="1.5" fill="rgba(255,215,0,0.15)"/>
    <line x1="29" y1="23" x2="37" y2="23" stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M29,23 Q33,30 37,23" stroke="#FFD700" strokeWidth="1.5" fill="rgba(255,215,0,0.15)"/>
  </svg>
);

// Real trishul from DevaKathaLS9 — scaled to fit inline
const TrishulIcon: React.FC = () => (
  <svg width="28" height="54" viewBox="0 0 203 392" fill="none">
    <g fill="#FFD700" fillOpacity="0.92">
      <path d="M33.499,144.251c3.662-1.943,9.738-1.436,14.772,0.443c10.396-18.138,17.586-81.09-7.309-95.717c15.417,42.394-6.783,76.49-8.583,79.536l-1.114,1.678l-0.77,1.2C32.434,135.627,33.327,139.967,33.499,144.251z" />
      <path d="M152.59,143.94c4.398-0.919,9.115-1.308,12.162,0.308c0.21-5.238,1.496-11.057,4.456-15.503l-0.154-0.232c-1.802-3.046-24-37.142-8.584-79.536c-25.053,14.719-18.396,77.261-7.884,94.954L152.59,143.94z" />
      <path d="M99.301,0.088c-0.021,13.632-5.121,89.769-19.03,119.995l-0.816,1.81c0.324-0.011,1.201-0.07,1.525-0.07c6.682,0.015,13.681,2.289,18.766,7.109c4.964-4.706,12.894-6.789,19.436-6.906l-0.848-1.942C104.425,89.857,99.322,13.72,99.301,0.088z" />
      <path d="M192.981,205.221c-0.852-2.601-3.635-17.944-6.557-31.677c-1.246-5.87-13.908-44.277-0.488-44.313c-13.692-8.271-18.371,8.438-18.79,18.87c-4.964-2.634-14.366-2.616-19.591,2.38c-2.824,2.701-3.393,9.954-1.215,12.029c6.166-11.215,15.965,6.552,18.42,11.946c4.434,9.729,6.271,32.455-3.207,39.996c-12.093,9.625-30.44,1.15-44.426-10.643l-9.499,5.417c0.391,1.834,0.606,3.822,0.608,5.908c-0.004,4.316-0.905,8.235-2.439,11.177c-0.214,0.407-0.444,0.796-0.687,1.167c-0.055,0.084-0.113,0.16-0.169,0.242c-0.202,0.294-0.41,0.578-0.63,0.844c-0.055,0.065-0.11,0.128-0.166,0.192c-0.242,0.278-0.493,0.542-0.757,0.782c-0.036,0.034-0.073,0.068-0.109,0.102c-0.308,0.271-0.626,0.52-0.959,0.732h0.001c-0.948,0.607-1.998,0.972-3.15,0.983c-1.115-0.011-2.136-0.349-3.06-0.92l0,0c-0.337-0.208-0.659-0.454-0.969-0.723c-0.06-0.052-0.117-0.107-0.175-0.16c-0.247-0.226-0.484-0.469-0.712-0.729c-0.073-0.082-0.146-0.165-0.216-0.251c-0.21-0.254-0.411-0.524-0.604-0.806c-0.065-0.094-0.133-0.184-0.197-0.28c-0.244-0.375-0.478-0.766-0.693-1.177c-1.535-2.941-2.436-6.86-2.44-11.177c0.002-2.353,0.278-4.578,0.771-6.6l-9.19-5.191c-14.018,12.034-32.645,20.859-44.896,11.108c-9.475-7.541-7.639-30.267-3.207-39.996c2.457-5.394,12.255-23.161,18.421-11.946c2.176-2.075,1.608-9.328-1.217-12.029c-5.224-4.997-14.626-5.014-19.589-2.38c-0.42-10.432-5.1-27.141-18.791-18.87c13.418,0.036,0.758,38.443-0.491,44.313c-2.921,13.733-5.705,29.076-6.556,31.677c-0.908,2.77-1.59,4.088-5.361,7.082c2.862,3.139,4.91,7.863,5.816,9.951c2.866,6.609,2.959,29.955,20.896,37.252c32.638,13.279,50.169-27.006,59.392-28.898c5.418,15.303-10.312,33.072-10.312,33.072l6.433,4.622c0,0,16.885,81.185-6.433,118.187c-11.437,18.147,57.514,16.35,46.936,0c-23.618-36.502-6.433-118.187-6.433-118.187l6.433-4.251c0,0-15.393-18.181-10.144-33.523c10.059,3.403,30.145,41.37,59.047,28.979c17.797-7.633,18.028-30.643,20.896-37.25c0.905-2.09,2.955-6.814,5.815-9.953C194.572,209.311,193.889,207.993,192.981,205.221z" />
      <path d="M99.172,134.413c-10.139-14.514-39.866-11.587-36.586,9.843c18.671-15.452,35.253,19.54,18.406,31.885c3.329,0.551,12.355,11.666-3.736,20.307l14.919,8.288c0.121-0.263,0.24-0.528,0.37-0.777c1.52-2.907,3.774-5.019,6.627-5.046c2.85,0.027,5.105,2.139,6.625,5.046c0.166,0.318,0.321,0.653,0.473,0.994l14.527-8.504c-15.021-10.12-6.777-19.756-3.447-20.307c-16.846-12.345-0.266-47.337,18.406-31.885C139.036,122.826,109.309,119.899,99.172,134.413z" />
    </g>
  </svg>
);

const LotusIcon: React.FC = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
    <path d="M18 26 Q14 18 18 10 Q22 18 18 26Z" stroke="#FFD700" strokeWidth="1.2" fill="rgba(255,215,0,0.15)" strokeLinecap="round"/>
    <path d="M18 22 Q10 16 8 8 Q16 14 18 22Z" stroke="#FFD700" strokeWidth="1.2" fill="rgba(255,215,0,0.1)" strokeLinecap="round"/>
    <path d="M18 22 Q4 20 4 12 Q12 18 18 22Z" stroke="#FFD700" strokeWidth="1" fill="rgba(255,215,0,0.07)" strokeLinecap="round"/>
    <path d="M18 22 Q26 16 28 8 Q20 14 18 22Z" stroke="#FFD700" strokeWidth="1.2" fill="rgba(255,215,0,0.1)" strokeLinecap="round"/>
    <path d="M18 22 Q32 20 32 12 Q24 18 18 22Z" stroke="#FFD700" strokeWidth="1" fill="rgba(255,215,0,0.07)" strokeLinecap="round"/>
    <line x1="18" y1="26" x2="18" y2="32" stroke="#FFD700" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="8" y1="30" x2="28" y2="30" stroke="rgba(255,215,0,0.4)" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

const ICONS = [ScalesIcon, TrishulIcon, LotusIcon];

export const MantraCard: React.FC<{
  data: MantraCardData;
  durationInFrames: number;
  startFrame?: number;
}> = ({ data, durationInFrames, startFrame = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const localFrame = frame - startFrame;
  if (localFrame < 0 || localFrame >= durationInFrames) return null;

  const fadeOutStart = durationInFrames - Math.round(fps * 1.0);
  const cardOpacity = interpolate(localFrame, [fadeOutStart, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
    easing: Easing.bezier(0.4, 0, 0.2, 1),
  });

  return (
    <AbsoluteFill style={{ opacity: cardOpacity, pointerEvents: "none" }}>
      {data.words.map((w, i) => {
        const slot = SLOTS[i % SLOTS.length];
        const wordFrame = Math.round((w.offset_ms / 1000) * fps);
        const revealDur = Math.round(fps * 0.5);
        const lineDur   = Math.round(fps * 0.4);
        const meanDur   = Math.round(fps * 0.35);

        const wordOpacity = interpolate(localFrame, [wordFrame, wordFrame + revealDur], [0, 1], {
          extrapolateLeft: "clamp", extrapolateRight: "clamp",
          easing: Easing.bezier(0.16, 1, 0.3, 1),
        });
        const wordY = interpolate(localFrame, [wordFrame, wordFrame + revealDur], [-28, 0], {
          extrapolateLeft: "clamp", extrapolateRight: "clamp",
          easing: Easing.bezier(0.16, 1, 0.3, 1),
        });

        const lineProgress = interpolate(localFrame, [wordFrame + 4, wordFrame + 4 + lineDur], [0, 1], {
          extrapolateLeft: "clamp", extrapolateRight: "clamp",
          easing: Easing.bezier(0.4, 0, 0.2, 1),
        });

        const meanOpacity = interpolate(localFrame, [wordFrame + meanDur, wordFrame + meanDur + Math.round(fps * 0.4)], [0, 1], {
          extrapolateLeft: "clamp", extrapolateRight: "clamp",
        });

        const glowOpacity = interpolate(localFrame,
          [wordFrame, wordFrame + 3, wordFrame + Math.round(fps * 1.2)],
          [0, 0.7, 0.2],
          { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
        );

        return (
          // Outer anchor at section centre; translateX(-50%) centres the block horizontally
          <div key={i} style={{ position: "absolute", left: slot.x, top: slot.y, transform: "translateX(-50%)" }}>

            {/* Radial glow burst — centred behind word */}
            <div style={{
              position: "absolute",
              width: 320, height: 160,
              left: -160, top: -40,
              borderRadius: "50%",
              background: `radial-gradient(ellipse, rgba(255,215,0,${glowOpacity * 0.5}) 0%, transparent 70%)`,
              filter: "blur(18px)",
              pointerEvents: "none",
            }} />

            <div style={{
              opacity: wordOpacity,
              transform: `translateY(${wordY}px)`,
              willChange: "transform, opacity",
              textAlign: "center",
            }}>
              {/* Word */}
              <div style={{
                fontFamily: devFont,
                fontSize: 88,
                fontWeight: 700,
                color: GOLD,
                lineHeight: 1,
                letterSpacing: -1,
                textShadow: `0 0 60px rgba(255,215,0,0.5), 0 0 20px rgba(255,215,0,0.3), 0 4px 12px rgba(0,0,0,0.8)`,
                whiteSpace: "nowrap",
              }}>
                {w.word}
              </div>

              {/* Icon centred, with symmetric underlines growing outward */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 8 }}>
                <div style={{
                  height: 1.5,
                  width: `${lineProgress * 72}px`,
                  background: `linear-gradient(to left, ${GOLD}, transparent)`,
                  opacity: 0.5,
                  flexShrink: 0,
                }} />
                {(() => { const Icon = ICONS[i % ICONS.length]; return <Icon />; })()}
                <div style={{
                  height: 1.5,
                  width: `${lineProgress * 72}px`,
                  background: `linear-gradient(to right, ${GOLD}, transparent)`,
                  opacity: 0.5,
                  flexShrink: 0,
                }} />
              </div>

              {/* Meaning */}
              <div style={{
                marginTop: 10,
                opacity: meanOpacity,
                fontFamily: inter,
                fontSize: 11,
                fontWeight: 300,
                color: "rgba(255,215,0,0.55)",
                letterSpacing: 5,
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}>
                {MEANINGS[i] ?? ""}
              </div>
            </div>

          </div>
        );
      })}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
